import type { Admin } from '@/services/types';

export const hasAdminPermission = (permissions: string[], required: string) => {
  if (permissions.includes('*') || permissions.includes(required)) return true;

  switch (required) {
    case 'admins.read':
      return permissions.includes('admins.manage');
    case 'users.read':
      return [
        'users.write',
        'users.moderate',
        'users.delete',
        'payments.fulfill',
      ].some((permission) => permissions.includes(permission));
    case 'videos.read':
      return ['videos.write', 'videos.delete', 'content.manage'].some(
        (permission) => permissions.includes(permission),
      );
    case 'moderation.read':
      return permissions.includes('moderation.manage');
    case 'payments.read':
      return permissions.includes('payments.fulfill');
    default:
      return false;
  }
};

export const canGrantAdminPermissions = (
  actorPermissions: string[],
  targetPermissions: string[],
) =>
  targetPermissions.every((permission) =>
    hasAdminPermission(actorPermissions, permission),
  );

export default function access(
  initialState: { currentUser?: Admin } | undefined,
) {
  const { currentUser } = initialState ?? {};
  const isLoggedIn = !!currentUser;
  const permissions = currentUser?.permissions ?? [];
  const has = (...required: string[]) =>
    isLoggedIn &&
    required.some((permission) => hasAdminPermission(permissions, permission));

  const canViewUsers = has(
    'users.read',
    'users.write',
    'users.moderate',
    'users.delete',
  );
  const canManageUserGroups = has('user_groups.manage');
  const canViewAdmin = has('admins.read', 'admins.manage');
  const canEditAdmin = has('admins.manage');
  const canAccessUsers = canViewUsers || canManageUserGroups || canViewAdmin;
  const canViewVideos = has('videos.read', 'videos.write', 'videos.delete');
  const canManageVideoInfra = has('video_infra.manage');
  const canAccessVideos = canViewVideos || canManageVideoInfra;
  const canViewModeration = has('moderation.read', 'moderation.manage');
  const canManageContent = has('content.manage');
  const canManageNotices = has('notices.manage');
  const canManageAdverts = has('adverts.manage');
  const canReadPayments = has('payments.read');
  const canConfigurePayments = has('payments.configure');
  const canFulfillPayments = has('payments.fulfill');
  const canFulfillMemberships = canFulfillPayments && has('users.read');
  const canManageSearch = has('system.search');
  const canSystemConfig = has('system.config');
  const canManageVersions = has('system.versions');
  const canManageAvatars = has('system.avatars');

  return {
    isLoggedIn,
    canAccessUsers,
    canViewUsers,
    canEditUser: has('users.write'),
    canBanUser: has('users.moderate'),
    canModerateUser: has('users.moderate'),
    canDeleteUser: has('users.delete'),
    canManageUserGroups,
    canViewAdmin,
    canEditAdmin,
    canCreateAdmin: canEditAdmin,
    canDeleteAdmin: canEditAdmin,
    canAccessVideos,
    canViewVideos,
    canCreateVideo: has('videos.write'),
    canEditVideo: has('videos.write'),
    canDeleteVideo: has('videos.delete'),
    canManageVideoInfra,
    canAccessContent: canManageContent || has('moderation.config'),
    canManageContent,
    canAccessComments: canViewModeration || has('moderation.config'),
    canViewModeration,
    canManageModeration: has('moderation.manage'),
    canModerationConfig: has('moderation.config'),
    canAccessOperations: canManageNotices || canManageAdverts,
    canManageNotices,
    canManageAdverts,
    canAccessPay:
      canReadPayments || canConfigurePayments || canFulfillMemberships,
    canReadPayments,
    canConfigurePayments,
    canFulfillPayments,
    canFulfillMemberships,
    canAccessSystem:
      canManageSearch ||
      canSystemConfig ||
      canManageVersions ||
      canManageAvatars,
    canManageSearch,
    canSystemConfig,
    canManageVersions,
    canManageAvatars,
  };
}
